import PropTypes from 'prop-types';
import React, { useReducer, useMemo, useEffect, useCallback } from 'react';
import Modal from '@strava/ui/Modal';
import { useHistory } from 'react-router-dom';
import { trackV2 } from 'utils/analytics';
import { isDev, devLog } from 'utils/dev-utils';
import { isNil } from 'lodash-es';

import { reducer } from './state/Onboarding.reducer';
import {
  CONSENTS,
  CONSENTS_FLOW,
  ONBOARDING,
  REGISTRATION_FLOW
} from './shared/onboarding-constants';
import { OnboardingContextProvider } from './state/OnboardingContext';
import { stepToUrl } from './shared/onboarding-utils';
import RegistrationFlowRoutes from './components/RegistrationFlowRoutes';
import ConsentsFlowRoutes from './components/ConsentsFlowRoutes';
import NextStepButton from './components/NextStepButton/NextStepButton';

const Onboarding = ({
  page,
  currentAthlete,
  step,
  fbAppId,
  fbVersion,
  locale
}) => {
  const history = useHistory();

  const currentStep =
    page === ONBOARDING
      ? REGISTRATION_FLOW.createProfile
      : CONSENTS_FLOW.welcome;

  const [state, dispatch] = useReducer(reducer, {
    currentAthlete,
    currentStep,
    fbAppId,
    fbVersion,
    locale,
    page,
    alert: null
  });

  const track = useCallback(
    (fields) => {
      trackV2({
        category: page, // TODO [fumba] - change prop (page) to category
        page: currentStep,
        ...fields,
        properties: {
          is_mobile: window.navigator.userAgent.includes('Mobile'),
          ...fields.properties
        }
      });
    },
    [currentStep, page]
  );

  const providerState = useMemo(
    () => ({
      state,
      dispatch,
      track
    }),
    [state, track]
  );

  // For local development only
  // See README#local-development for more details
  useEffect(() => {
    if (!isNil(step) && isDev() && page === ONBOARDING) {
      const path = stepToUrl({ page, step });
      history.push(path);
      devLog(`[Onboarding - DEV] routing to path - ${path}`);
    } else {
      devLog(`[Onboarding] routing to path - ${window.location.pathname}`);
    }
  }, [history, page, step]);

  return (
    <OnboardingContextProvider value={providerState}>
      <Modal aria-labelledby="onboarding-step-title" isOpen={true}>
        {page === ONBOARDING && <RegistrationFlowRoutes />}
        {page === CONSENTS && <ConsentsFlowRoutes />}

        <NextStepButton />
      </Modal>
    </OnboardingContextProvider>
  );
};

Onboarding.defaultProps = {
  step: null
};

Onboarding.propTypes = {
  page: PropTypes.oneOf([ONBOARDING, CONSENTS]).isRequired,
  currentAthlete: PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string
  }).isRequired,
  step: PropTypes.string,
  fbAppId: PropTypes.number.isRequired,
  fbVersion: PropTypes.string.isRequired,
  locale: PropTypes.string.isRequired
};

export default Onboarding;
